import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useActiveEventStore } from '@/hooks/useActiveEvent';

export default function Login() {
  const { user, signIn, loading, error, configValid } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const clearActiveEvent = useActiveEventStore((s) => s.clear);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!configValid) {
    return <Navigate to="/config-required" replace />;
  }

  if (user) {
    const from = (location.state as { from?: string } | null)?.from || '/dashboard';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
      clearActiveEvent(); // force re-selection on new login
      navigate('/dashboard', { replace: true });
    } catch {
      // error already in state
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center text-white font-display font-bold text-section mb-4 shadow-primary-glow">
            O
          </div>
          <h1 className="text-headline font-display font-bold text-ink">Ophth POS</h1>
          <p className="text-body text-ink-subtle mt-2">
            Sign in to manage your event sales
          </p>
        </div>

        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-start gap-2 p-3 rounded bg-danger/5 border border-danger/20 text-small text-danger">
                <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Input
              label="Email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kamu@event.id"
              leftIcon={<Mail size={16} />}
            />

            <Input
              label="Password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              leftIcon={<Lock size={16} />}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={submitting || loading}
              leftIcon={<LogIn size={16} />}
            >
              Sign in
            </Button>
          </form>
        </div>

        <p className="text-center text-caption text-ink-subtle mt-6">
          Butuh bantuan? Hubungi owner event kamu.
        </p>
      </div>
    </div>
  );
}
